/**
 * Milliseconds in one second, for converting wire seconds to and from JavaScript time.
 *
 * @since 1.3.0
 */
export const MS_PER_SECOND = 1000;

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86_400;
const COMPACT_THRESHOLD = 1000;
const COMPACT_ROUNDING_LIMIT = 100;

/**
 * Every timestamp on the wire is an integer count of seconds since the epoch. Changing that
 * assumption is a one-line change here rather than a sweep through the views.
 *
 * @since 1.0.0
 * @param epochSeconds - a wire timestamp
 * @returns the equivalent Date
 */
export function toDate(epochSeconds: number): Date {
    return new Date(epochSeconds * MS_PER_SECOND);
}

/**
 * Reads the current wire clock.
 *
 * @since 1.0.0
 * @returns now, in whole seconds since the epoch
 */
export function nowSeconds(): number {
    return Math.floor(Date.now() / MS_PER_SECOND);
}

/**
 * Formats how long ago something happened as a single compact unit: `40s`, `18m`, `2h` or `3d`.
 *
 * @since 1.0.0
 * @param epochSeconds - when it happened
 * @param reference - the moment to measure against, defaulting to now
 * @returns the age without a trailing "ago"
 */
export function formatRelativeAge(epochSeconds: number, reference: number = nowSeconds()): string {
    const elapsed = Math.max(0, reference - epochSeconds);

    if (elapsed < SECONDS_PER_MINUTE) {
        return `${elapsed}s`;
    }

    if (elapsed < SECONDS_PER_HOUR) {
        return `${Math.floor(elapsed / SECONDS_PER_MINUTE)}m`;
    }

    if (elapsed < SECONDS_PER_DAY) {
        return `${Math.floor(elapsed / SECONDS_PER_HOUR)}h`;
    }

    return `${Math.floor(elapsed / SECONDS_PER_DAY)}d`;
}

/**
 * Formats a timestamp as a 24-hour clock time.
 *
 * @since 1.0.0
 * @param epochSeconds - the timestamp
 * @returns a time such as `17:31`
 */
export function formatClock(epochSeconds: number): string {
    return toDate(epochSeconds).toLocaleTimeString(undefined, { hour: '2-digit', hourCycle: 'h23', minute: '2-digit' });
}

/**
 * Formats a timestamp as an abbreviated month and day.
 *
 * @since 1.0.0
 * @param epochSeconds - the timestamp
 * @returns a date such as `Aug 16`
 */
export function formatMonthDay(epochSeconds: number): string {
    return toDate(epochSeconds).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/**
 * Formats a timestamp the way the alert tables do: relative wording for today, an explicit
 * date otherwise.
 *
 * @since 1.0.0
 * @param epochSeconds - the timestamp
 * @param reference - the moment that defines "today", defaulting to now
 * @returns a label such as `today 17:12` or `Aug 16 08:10`
 */
export function formatTimestamp(epochSeconds: number, reference: number = nowSeconds()): string {
    const isToday = toDate(epochSeconds).toDateString() === toDate(reference).toDateString();

    if (isToday) {
        return `today ${formatClock(epochSeconds)}`;
    }

    return `${formatMonthDay(epochSeconds)} ${formatClock(epochSeconds)}`;
}

/**
 * Formats the span between two timestamps.
 *
 * @since 1.0.0
 * @param fromSeconds - the earlier timestamp
 * @param toSeconds - the later timestamp
 * @returns a duration such as `50m` or `1h 37m`
 */
export function formatDuration(fromSeconds: number, toSeconds: number): string {
    const elapsed = Math.max(0, toSeconds - fromSeconds);
    const hours = Math.floor(elapsed / SECONDS_PER_HOUR);
    const minutes = Math.floor((elapsed % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);

    if (hours === 0) {
        return `${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
}

/**
 * Abbreviates a large count so it fits a KPI tile.
 *
 * @since 1.0.0
 * @param value - the count
 * @returns a label such as `842` or `14.2k`
 */
export function formatCompactCount(value: number): string {
    if (value < COMPACT_THRESHOLD) {
        return String(value);
    }

    const thousands = value / COMPACT_THRESHOLD;
    const rounded = thousands >= COMPACT_ROUNDING_LIMIT ? Math.round(thousands) : Number(thousands.toFixed(1));

    return `${rounded}k`;
}

/**
 * Formats a millisecond duration as seconds to one decimal place.
 *
 * @since 1.0.0
 * @param milliseconds - the duration
 * @returns a label such as `4.1s`
 */
export function formatSeconds(milliseconds: number): string {
    return `${(milliseconds / MS_PER_SECOND).toFixed(1)}s`;
}

/**
 * Picks the singular or plural form of a noun.
 *
 * @since 1.0.0
 * @param count - how many there are
 * @param singular - the singular form
 * @param pluralForm - the plural form, defaulting to the singular plus an `s`
 * @returns the form matching the count
 */
export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
    return count === 1 ? singular : pluralForm;
}

/**
 * Strips the scheme and any trailing slash from a URL so a site column reads as a bare domain.
 *
 * @since 1.0.0
 * @param url - the site URL
 * @returns the host and path without the scheme
 */
export function formatSiteUrl(url: string): string {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}
